import { FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import {
  ICadProperty,
  ICadPropertyCategory,
} from 'Data/interfaces/CadViewer/ICadProperty';
import {
  PanelWrapper,
  HeaderItem,
  CategoryPanel,
  PropertyRow,
  VariedRow,
  SubPropertyList,
  SubPropertyRow,
  EmptyState,
} from './styles';

const { Panel } = Collapse;

export interface IViewerPropertiesPanelProps {
  name?: string;
  categories: ICadPropertyCategory[];
  emptyMessage?: string;
  emptyImage?: string;
}

const propKey = (categoryName: string, label: string, index: number) =>
  `${categoryName}::${label}::${index}`;

const ViewerPropertiesPanel: FC<IViewerPropertiesPanelProps> = ({
  name,
  categories,
  emptyMessage,
  emptyImage,
}) => {
  const { t } = useTranslation('federatedViewer');
  const resolvedEmptyMessage = emptyMessage ?? t('properties.noObjectSelected');
  const variedLabel = t('properties.variedLabel');

  const [openVaried, setOpenVaried] = useState<Set<string>>(new Set());

  const toggleVaried = useCallback((key: string) => {
    setOpenVaried(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (!categories || categories.length === 0) {
    return (
      <EmptyState>
        {emptyImage && (
          <img src={emptyImage} alt={resolvedEmptyMessage} />
        )}
        <p>{resolvedEmptyMessage}</p>
      </EmptyState>
    );
  }

  return (
    <PanelWrapper>
      {name && (
        <HeaderItem>
          <span className="headerLabel">Name</span>
          <span className="headerValue" title={name}>{name}</span>
        </HeaderItem>
      )}
      <CategoryPanel>
        {categories.map((category, categoryIndex) => (
          <Collapse
            key={`${category.categoryName}-${categoryIndex}`}
            bordered={false}
            defaultActiveKey={[category.categoryName]}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel
              key={category.categoryName}
              header={category.categoryName}
            >
              {category.properties.map((prop, propIndex) => {
                const key = propKey(category.categoryName, prop.label, propIndex);
                return renderPropertyRow(
                  prop,
                  key,
                  openVaried.has(key),
                  toggleVaried,
                  variedLabel
                );
              })}
            </Panel>
          </Collapse>
        ))}
      </CategoryPanel>
    </PanelWrapper>
  );
};

function renderPropertyRow(
  prop: ICadProperty,
  key: string,
  isOpen: boolean,
  toggleVaried: (key: string) => void,
  variedLabel: string
) {
  if (prop.aggregation === 'mixed' && prop.values && prop.values.length > 0) {
    return (
      <div key={key}>
        <VariedRow
          className={isOpen ? 'is-open' : ''}
          onClick={() => toggleVaried(key)}
        >
          <span className="propLabelGroup">
            <CaretRightOutlined className="chevron" />
            <span className="propLabel" title={prop.label}>
              {prop.label}
            </span>
          </span>
          <span className="propValue" title={variedLabel}>
            {variedLabel}
          </span>
        </VariedRow>
        {isOpen && (
          <SubPropertyList>
            {prop.values.map((v, i) => (
              <SubPropertyRow key={`${key}-${i}`}>
                <span className="subIndex">#{i + 1}</span>
                <span className="subValue" title={v}>{v}</span>
              </SubPropertyRow>
            ))}
          </SubPropertyList>
        )}
      </div>
    );
  }

  const showSumBadge = prop.aggregation === 'sum' && (prop.count ?? 0) > 1;

  return (
    <PropertyRow key={key}>
      <span className="propLabel" title={prop.label}>
        {prop.label}
      </span>
      <span className="propValueRight">
        <span className="propValue" title={prop.value}>
          {prop.value}
        </span>
        {showSumBadge && (
          <span className="sumBadge">Σ {prop.count}</span>
        )}
      </span>
    </PropertyRow>
  );
}

export default ViewerPropertiesPanel;
